import { Avatar, Badge, BottomSheet, Button, Chip, DeltaPill, EmptyState, formatCount, formatDate, formatMoney, formatTime, Icon, LargeTitle, PullToRefresh, Sparkline, Skeleton, StrikeText, SwipeRow, Tick, useToast, type ChipKind } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { createTask, doneFollowup, doneTask, reopenTask, snoozeFollowup, updateAppointment, type AppointmentAction, type AppointmentItem, type FollowupItem, type TaskItem } from "../../../api/home";
import { useLargeTitleChrome } from "../../../app/mobile/chrome";
import { useSheetBack } from "../../../app/mobile/useSheetBack";
import { useServices } from "../../../app/services";
import { StaffName } from "../../directory/StaffName";
import { addDays, minutesOfDay, today as localDate } from "../../../lib/dates";
import { isUnmasked, telHref, whatsappHref } from "../../../lib/phone";
import { useAppointments, useDues, useFollowups, useHomeScope, useRangeData, useTasks, type Scope } from "../hooks";
import { countBills, countVisits, deltaTone, hourlyCollections, percentChange, totalCollected, upToMinute } from "../metrics";
import { CollectionsCard, NotConnectedCard, RenewalsCard, ScheduleCard } from "../Panels";
import { FailureBody } from "../states";
import { mhome as t } from "./strings";
import s from "./mobilehome.module.css";

/** Keys the pull-to-refresh invalidates: exactly what Home reads, nothing else. */
const HOME_KEYS = new Set(["followups", "appointments", "tasks", "collections", "bills", "dues", "checkins", "occurrences"]);
/** The Undo window. The call goes out when it closes (there is no server-side undo for done or snooze). */
export const UNDO_MS = 5000;

const chipKind = (kind: string): ChipKind => (kind === "renewal" ? "renewal" : kind === "due" ? "dues" : kind === "inactive" ? "inactive" : kind === "lead_trial" ? "trial" : "enquiry");
const dueNow = (items: readonly FollowupItem[], date: string) => items.filter((f) => f.overdue || f.dueDate === null || f.dueDate <= date);
type Seg = "followups" | "appts" | "tasks";

function greeting(sc: Scope) {
  const h = Math.floor(minutesOfDay(sc.nowMs, sc.ctx.timeZone) / 60);
  return h < 12 ? t.greeting.morning : h < 17 ? t.greeting.afternoon : t.greeting.evening;
}

function dueLabel(f: FollowupItem, sc: Scope): string {
  const { locale, timeZone } = sc.ctx;
  const day = f.dueDate ?? (f.dueAt !== null ? localDate(f.dueAt, timeZone) : null);
  if (!day) return "";
  if (day === sc.date) return f.dueAt !== null ? formatTime(f.dueAt, timeZone) : formatDate(day, locale, "weekday");
  return day === addDays(sc.date, -1) ? "Yesterday" : formatDate(day, locale, "weekday");
}
function detailText(f: FollowupItem, sc: Scope): string {
  const bits: string[] = [t.followups.kinds[f.kind] ?? "Follow-up"];
  if (f.kind === "due" && f.amountDue !== undefined && sc.ctx.me) bits.push(`${formatMoney(f.amountDue, sc.ctx.me.business.currency, sc.ctx.locale)} due`);
  if (f.dueDate && f.kind === "renewal") bits.push(`plan ends ${formatDate(f.dueDate, sc.ctx.locale, "weekday")}`);
  return bits.join(" · ");
}

const SkeletonRows = ({ n = 3 }: { n?: number }) => (
  <div className={s.grp} aria-busy="true">{Array.from({ length: n }, (_, i) => <div className={s.skRow} key={i}><Skeleton width={40} height={40} style={{ borderRadius: "50%" }} /><div><Skeleton width="60%" height={14} /><Skeleton width="85%" height={12} /></div></div>)}</div>
);

/* ------------------------------------------------------------------ the page */

export function MobileHome() {
  const sc = useHomeScope();
  const { ctx } = sc;
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const fu = useFollowups(sc);
  const ap = useAppointments(sc);
  const tk = useTasks(sc);
  const wantsTask = !!sp.get("task");
  const { key: navKey } = useLocation();
  const [seg, setSeg] = useState<Seg>(wantsTask ? "tasks" : "followups");
  // Add task from the + sheet lands here while Home is already mounted: each tap is a new location, so each one counts.
  useEffect(() => { if (wantsTask) setSeg("tasks"); }, [wantsTask, navKey]);
  const first = ctx.me?.staff.name.split(/\s+/)[0];
  const title = `${greeting(sc)}${first ? `, ${first}` : ""}`;
  const onUnder = useLargeTitleChrome(title);
  const branchName = ctx.me ? (ctx.me.branches.find((b) => b.id === sc.branchId)?.name ?? ctx.me.branch?.name ?? "") : "";

  const overdue = (fu.data?.items ?? []).filter((f) => f.overdue).length;
  const nowMin = minutesOfDay(sc.nowMs, ctx.timeZone);
  const apItems = ap.data?.items ?? [];
  const nextTrial = [...apItems].sort((a, b) => a.startsAt - b.startsAt).find((a) => a.kind === "Trial" && a.status === "booked" && minutesOfDay(a.startsAt, ctx.timeZone) >= nowMin);
  const left = apItems.filter((a) => a.status === "booked" && minutesOfDay(a.startsAt, ctx.timeZone) >= nowMin).length;
  const count = (n: number | undefined) => (n === undefined ? undefined : formatCount(n, ctx.locale));

  return (
    <PullToRefresh onRefresh={() => qc.invalidateQueries({ predicate: (q) => HOME_KEYS.has(String(q.queryKey[0])) })}>
      <div className={s.page}>
        <title>Home · Helmr</title>
        <div className={s.hero}>
        <LargeTitle eyebrow={`${formatDate(sc.date, ctx.locale, "long")}${branchName ? ` · ${branchName}` : ""}`} title={title} onUnderBarChange={onUnder} />

        <section className={s.sum} aria-label={t.summaryLabel} aria-live="polite">
          {fu.data ? (
            <button type="button" onClick={() => setSeg("followups")}><i className={`${s.sd} ${overdue ? s.crit : ""}`} />{overdue ? <b>{t.summary.overdue(overdue)}</b> : t.summary.noneOverdue}<Icon name="chevR" size={16} className={s.chev} /></button>
          ) : null}
          {ap.data ? (
            <button type="button" onClick={() => setSeg("appts")}><i className={`${s.sd} ${s.info}`} />{nextTrial ? <span>{t.summary.nextTrial} <b>{nextTrial.person.name}</b> {t.summary.at} <b>{formatTime(nextTrial.startsAt, ctx.timeZone)}</b></span> : t.summary.left(left)}<Icon name="chevR" size={16} className={s.chev} /></button>
          ) : null}
          {!fu.data && !ap.data ? <div className={s.sumSk} aria-busy="true"><Skeleton width="70%" height={14} /><Skeleton width="55%" height={14} /></div> : null}
        </section>
        </div>

        <MetricCarousel sc={sc} />

        <div className={s.segw}>
          <div className={s.seg} role="tablist" aria-label={t.seg.label}>
            {([["followups", t.seg.followups, count(fu.data && dueNow(fu.data.items, sc.date).length), overdue > 0], ["appts", t.seg.appts, count(ap.data?.items.length), false], ["tasks", t.seg.tasks, count(tk.data?.filter((x) => x.doneAt === null).length), false]] as const).map(([key, label, n, crit]) => (
              <button key={key} type="button" role="tab" aria-selected={seg === key} onClick={() => setSeg(key)}>
                {label}{n !== undefined ? " " : null}{n !== undefined ? <span className={`${s.c} ${crit ? s.cCrit : ""}`}>{n}</span> : null}
              </button>
            ))}
          </div>
        </div>
        {seg === "followups" ? <p className={s.hintLine}><Icon name="chevR" size={14} />{t.swipe.hint}</p> : null}

        <div role="tabpanel" aria-label={t.seg[seg]}>
          {/* Stays mounted behind the other segments: unmounting sends whatever is inside its Undo window. */}
          <div hidden={seg !== "followups"}><MobileFollowups sc={sc} /></div>
          {seg === "appts" ? <MobileAppointments sc={sc} /> : seg === "tasks" ? <MobileTasks sc={sc} focusKey={wantsTask ? navKey : null} /> : null}
        </div>

        <div className={s.cards}>
          <RenewalsCard sc={sc} />
          <ScheduleCard sc={sc} />
          <CollectionsCard sc={sc} range="today" />
        </div>
      </div>
    </PullToRefresh>
  );
}

/* ------------------------------------------------------------------ metric carousel */

type DeltaProps = React.ComponentProps<typeof DeltaPill>;

/** One carousel card, as in the mockup: label and delta, a 30px figure, a note and a full-bleed sparkline. */
function MCard({ label, value, note, delta, spark, sparkTone }: { label: string; value: React.ReactNode; note?: React.ReactNode; delta?: DeltaProps; spark?: readonly number[]; sparkTone?: "neutral" | "good" | "bad" }) {
  return (
    <div className={s.mcIn}>
      <div className={s.mh}><span className={s.lb}>{label}</span>{delta ? <DeltaPill {...delta} /> : null}</div>
      <b className={s.v}>{value}</b>
      {note ? <span className={s.n}>{note}</span> : null}
      {spark && spark.length > 1 ? <div className={s.spark}><Sparkline data={spark} tone={sparkTone} /></div> : <div className={s.sparkGap} />}
    </div>
  );
}

function MetricCarousel({ sc }: { sc: Scope }) {
  const d = useRangeData(sc, "today");
  const dues = useDues(sc);
  const { ctx } = sc;
  const [idx, setIdx] = useState(0);
  const rail = useRef<HTMLDivElement>(null);
  // Null: withheld by the server (a D57 field lever is off), shown as a dash.
  const money = (v: number | null) => (ctx.me && v !== null ? formatMoney(v, ctx.me.business.currency, ctx.locale) : "—");
  const nowMin = minutesOfDay(sc.nowMs, ctx.timeZone);
  const soFar = <T,>(rows: readonly T[], at: (r: T) => number) => upToMinute(rows, at, ctx.timeZone, nowMin);
  const delta = (pct: number | null) => (pct === null ? { tone: "flat" as const, children: "New" } : { tone: deltaTone(pct), direction: pct > 0 ? ("up" as const) : pct < 0 ? ("down" as const) : undefined, children: `${Math.abs(pct)}%` });

  const cards: Array<{ id: string; node: React.ReactNode }> = [];
  if (d.canPay && d.paymentsCur.data && d.paymentsPrev.data) {
    const cur = totalCollected(d.paymentsCur.data.collections);
    const prev = totalCollected(soFar(d.paymentsPrev.data.collections, (p) => p.createdAt));
    const pct = percentChange(cur, prev);
    const hours = hourlyCollections(d.paymentsCur.data.collections, ctx.timeZone);
    cards.push({ id: "collections", node: <MCard label={t.metrics.collections} value={`${money(cur)}${d.paymentsCur.data.capped ? "+" : ""}`} note={t.metrics.vs(money(prev), t.metrics.lastWeek)} delta={delta(pct)} spark={hours.values.length > 1 ? hours.values : undefined} sparkTone={pct === null || pct === 0 ? "neutral" : deltaTone(pct) === "good" ? "good" : "bad"} /> });
  }
  if (d.canBills && d.billsCur.data && d.billsPrev.data) {
    const cur = countBills(d.billsCur.data.bills);
    const prev = countBills(soFar(d.billsPrev.data.bills, (b) => b.createdAt));
    cards.push({ id: "bills", node: <MCard label={t.metrics.bills} value={d.billsCur.data.nextCursor ? `${cur}+` : cur} note={t.metrics.vsCount(prev, t.metrics.lastWeek)} delta={delta(percentChange(cur, prev))} /> });
  }
  if (d.canVisits && d.checkinsCur.data && d.checkinsPrev.data) {
    const cur = countVisits(d.checkinsCur.data.checkins);
    const prev = countVisits(soFar(d.checkinsPrev.data.checkins, (c) => c.scannedAt));
    cards.push({ id: "checkins", node: <MCard label={t.metrics.checkins} value={d.checkinsCur.data.nextCursor ? `${cur}+` : cur} note={t.metrics.vsCount(prev, t.metrics.lastWeek)} delta={delta(percentChange(cur, prev))} /> });
  }
  if (dues.enabled && dues.data) {
    // The server's totals cover every bill owing, not just the page (GET /dues, billing.dues).
    const { count, balance } = dues.data.totals;
    cards.push({ id: "dues", node: <MCard label={t.metrics.dues} value={money(balance)} note={t.metrics.owe(count, false)} /> });
  }
  const none = !d.canPay && !d.canBills && !d.canVisits && !dues.enabled;
  if (none) return null;
  cards.push({ id: "target", node: <NotConnectedCard title={t.target.title} what={t.target.what} /> });

  const pending = (d.canPay && d.paymentsCur.isPending) || (d.canBills && d.billsCur.isPending) || (d.canVisits && d.checkinsCur.isPending) || dues.isPending;
  const failure = [d.paymentsCur, d.paymentsPrev, d.billsCur, d.billsPrev, d.checkinsCur, d.checkinsPrev].find((q) => q.error)?.error ?? dues.error;

  const onScroll = () => {
    const el = rail.current;
    const card = el?.firstElementChild as HTMLElement | undefined;
    if (el && card) setIdx(Math.min(cards.length - 1, Math.round(el.scrollLeft / (card.offsetWidth + 10))));
  };
  const jump = (i: number) => {
    const el = rail.current;
    const card = el?.children[i] as HTMLElement | undefined;
    el?.scrollTo?.({ left: card ? card.offsetLeft - 20 : 0, behavior: "smooth" });
    setIdx(i);
  };

  return (
    <section aria-label={t.metrics.label}>
      <div className={s.sh}>
        <div><h2>{t.metrics.title}</h2><p>{formatDate(d.cur, ctx.locale, "long")}, as of {formatTime(sc.nowMs, ctx.timeZone)} · vs {formatDate(d.prev, ctx.locale, "long")}</p></div>
      </div>
      {pending ? (
        <div className={s.car} aria-busy="true"><div className={s.mc}><Skeleton width={90} height={13} /><Skeleton width="60%" height={28} /><Skeleton width="70%" height={12} /></div></div>
      ) : failure ? (
        <div className={s.grp}><FailureBody error={failure} title={t.metrics.error} what="The day's numbers" onRetry={() => { for (const q of [d.paymentsCur, d.paymentsPrev, d.billsCur, d.billsPrev, d.checkinsCur, d.checkinsPrev]) if (q.error) void q.refetch(); if (dues.error) dues.refetch(); }} /></div>
      ) : (
        <>
          <div className={s.car} ref={rail} onScroll={onScroll} tabIndex={0} role="region" aria-label={t.metrics.label}>
            {cards.map((c) => <div className={s.mc} key={c.id} data-metric={c.id}>{c.node}</div>)}
          </div>
          <div className={s.dots}>
            {cards.map((c, i) => <button key={c.id} type="button" aria-label={`${t.metrics.dotsLabel} ${i + 1} of ${cards.length}`} aria-current={i === idx} onClick={() => jump(i)}><i /></button>)}
          </div>
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ follow-ups */

function MobileFollowups({ sc }: { sc: Scope }) {
  const q = useFollowups(sc);
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<FollowupItem | null>(null);
  const last = useRef<FollowupItem | null>(null);
  if (open) last.current = open;
  useSheetBack(!!open, () => setOpen(null));
  const pending = useRef(new Map<string, { timer: ReturnType<typeof setTimeout>; run: () => Promise<void> }>());
  const run = useMutation({ mutationFn: ({ f, kind }: { f: FollowupItem; kind: "done" | "snooze" }) => (kind === "done" ? doneFollowup(client, f.key) : snoozeFollowup(client, f.key, addDays(sc.date, 1))) });

  const unhide = (key: string) => setHidden((x) => { const n = new Set(x); n.delete(key); return n; });
  // Leaving Home inside the Undo window must not lose the action: send what is pending, and replace the toast, whose Undo can no longer undo.
  useEffect(() => { const p = pending.current; return () => { if (p.size > 0) toast.show(t.swipe.saved); for (const [, e] of p) { clearTimeout(e.timer); void e.run(); } p.clear(); }; }, [toast]);

  function act(f: FollowupItem, kind: "done" | "snooze") {
    setHidden((x) => new Set(x).add(f.key));
    setOpen(null);
    const commit = async () => {
      pending.current.delete(f.key);
      try {
        await run.mutateAsync({ f, kind });
        void qc.invalidateQueries({ queryKey: ["followups"] });
      } catch {
        unhide(f.key);
        toast.show(t.followups.failed);
      }
    };
    pending.current.set(f.key, { timer: setTimeout(() => void commit(), UNDO_MS), run: commit });
    toast.show(kind === "done" ? t.swipe.doneToast(f.subject.name) : t.swipe.snoozeToast(f.subject.name), {
      durationMs: UNDO_MS,
      action: { label: t.swipe.undo, onAction: () => { const e = pending.current.get(f.key); if (e) clearTimeout(e.timer); pending.current.delete(f.key); unhide(f.key); } },
    });
  }

  const items = useMemo(() => dueNow(q.data?.items ?? [], sc.date).filter((f) => !hidden.has(f.key)), [q.data, hidden, sc.date]);
  const omitted = q.data?.omitted ?? [];
  if (q.isPending) return <SkeletonRows />;
  if (q.error) return <div className={s.grp}><FailureBody error={q.error} title={t.followups.error} what="Follow-ups" onRetry={() => void q.refetch()} /></div>;
  const sheet = open ?? last.current;
  return (
    <>
      <div className={s.grp}>
        {items.length === 0 ? <EmptyState compact icon="check" title={t.followups.empty} description={t.followups.emptyBody} /> : items.map((f) => (
          <SwipeRow key={f.key} name={f.subject.name}
            right={{ label: t.swipe.done, icon: "check", tone: "done", onCommit: () => act(f, "done") }}
            left={{ label: t.swipe.snooze, icon: "clock", tone: "snooze", onCommit: () => act(f, "snooze") }}>
            <div className={s.fg}>
              <Avatar id={f.subject.id} name={f.subject.name} />
              <span className={s.fgBody}>
                <button type="button" className={s.open} onClick={() => setOpen(f)} aria-haspopup="dialog" data-testid="followup-row">
                  <span className={s.l1}><b>{f.subject.name}</b><Chip kind={chipKind(f.kind)} /><span className={`${s.due} ${f.overdue ? s.od : ""}`}>{dueLabel(f, sc)}</span></span>
                  <small className={s.det}>{detailText(f, sc)}</small>
                </button>
                <span className={s.l3}>
                  <span className={s.own}><StaffName id={f.ownerStaffId} name={f.ownerName} you /></span>
                  {isUnmasked(f.subject.phone) ? (
                    <span className={s.acts}>
                      <a href={telHref(f.subject.phone)} aria-label={t.followups.callLabel(f.subject.name)}><Icon name="phone" size={20} /></a>
                      <a href={whatsappHref(f.subject.phone)} target="_blank" rel="noopener noreferrer" className={s.wa} aria-label={t.followups.whatsappLabel(f.subject.name)}><Icon name="chat" size={20} /></a>
                    </span>
                  ) : null}
                </span>
              </span>
            </div>
          </SwipeRow>
        ))}
      </div>
      {omitted.length > 0 ? <p className={s.note}>{t.followups.partialNote(omitted.join(" and "))}</p> : null}
      <BottomSheet open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }} title={sheet?.subject.name ?? ""} description={sheet ? detailText(sheet, sc) : undefined}>
        {sheet ? (
          <div className={s.sacts}>
            <Button onClick={() => act(sheet, "snooze")}><Icon name="clock" />{t.followups.snooze}</Button>
            <Button variant="primary" onClick={() => act(sheet, "done")}><Icon name="check" />{t.followups.done}</Button>
            {isUnmasked(sheet.subject.phone) ? <Button as="a" href={telHref(sheet.subject.phone)}><Icon name="phone" />{t.followups.call}</Button> : null}
            {isUnmasked(sheet.subject.phone) ? <Button as="a" href={whatsappHref(sheet.subject.phone)} target="_blank" rel="noopener noreferrer"><Icon name="chat" />{t.followups.whatsapp}</Button> : null}
          </div>
        ) : null}
      </BottomSheet>
    </>
  );
}

/* ------------------------------------------------------------------ appointments */

function actionsFor(a: AppointmentItem): Array<{ action: AppointmentAction; label: string; primary?: boolean }> {
  if (a.status === "done" || a.status === "cancelled" || a.status === "no_show") return [];
  const x = t.appointments.actions;
  switch (a.source) {
    case "pt": return [{ action: "no_show", label: x.no_show }, { action: "done", label: x.done, primary: true }];
    case "trial": return [{ action: "no_show", label: x.no_show }, { action: "done", label: x.trialDone, primary: true }];
    case "appointment": return [{ action: "cancelled", label: x.cancelled }, { action: "no_show", label: x.no_show }, ...(a.status === "booked" ? [{ action: "arrived" as const, label: x.arrived }] : []), { action: "done", label: x.done, primary: true }];
  }
}
const NODE = { in: "in", no_show: "ns" } as const;

function MobileAppointments({ sc }: { sc: Scope }) {
  const q = useAppointments(sc);
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const [busy, setBusy] = useState<AppointmentAction | null>(null);
  const last = useRef<AppointmentItem | null>(null);
  if (open) last.current = open;
  useSheetBack(!!open, () => setOpen(null));
  const { timeZone } = sc.ctx;
  const items = useMemo(() => [...(q.data?.items ?? [])].sort((a, b) => a.startsAt - b.startsAt), [q.data]);
  const nowMin = minutesOfDay(sc.nowMs, timeZone);
  const startMin = (a: AppointmentItem) => minutesOfDay(a.startsAt, timeZone);
  const next = items.find((a) => a.status === "booked" && startMin(a) >= nowMin);
  const nowIndex = items.findIndex((a) => startMin(a) >= nowMin);
  const a = open ?? last.current;

  async function go(action: AppointmentAction) {
    if (!a) return;
    setBusy(action);
    try {
      await updateAppointment(client, a, action, new Date(sc.nowMs).toISOString());
      await qc.invalidateQueries({ queryKey: ["appointments"] });
      toast.show(t.appointments.drawer.updated);
      setOpen(null);
    } catch { toast.show(t.appointments.drawer.failed); } finally { setBusy(null); }
  }

  if (q.isPending) return <SkeletonRows />;
  if (q.error) return <div className={s.grp}><FailureBody error={q.error} title={t.appointments.error} what="Appointments" onRetry={() => void q.refetch()} /></div>;
  if (items.length === 0) return <div className={s.grp}><EmptyState compact icon="trial" title={t.appointments.empty} description={t.appointments.emptyBody} /></div>;
  const nowLine = <div className={s.nowl} aria-hidden="true" data-now><span>{t.appointments.now(formatTime(sc.nowMs, timeZone))}</span></div>;
  return (
    <>
      <div className={`${s.grp} ${s.tl}`}>
        {items.map((x, i) => {
          const isNext = x === next;
          const past = startMin(x) < nowMin;
          return (
            <div key={`${x.source}:${x.id}`} style={{ display: "contents" }}>
              {i === nowIndex ? nowLine : null}
              <button type="button" className={`${s.ap} ${isNext ? s.next : ""} ${past ? s.past : ""}`} onClick={() => setOpen(x)} aria-haspopup="dialog" data-appt>
                <b>{formatTime(x.startsAt, timeZone)}</b>
                <span className={`${s.node} ${isNext ? "" : s[NODE[x.status as keyof typeof NODE] ?? "idle"]}`} />
                <span className={s.apBody}>
                  <span className={s.nm}>{x.person.name} <em>· {x.kind}</em>{isNext ? <> <Badge tone="info">{t.appointments.next}</Badge></> : null}</span>
                  {x.staff ? <span className={s.meta}><StaffName id={x.staff.id} name={x.staff.name} /></span> : null}
                </span>
                <Badge tone={({ booked: "info", in: "ok", no_show: "crit", done: "ok", cancelled: "neutral" } as const)[x.status]} dot>{t.appointments.status[x.status]}</Badge>
              </button>
            </div>
          );
        })}
        {nowIndex === -1 ? nowLine : null}
      </div>
      <BottomSheet open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }} title={a ? `${a.person.name} · ${a.kind}` : ""} description={a ? `${formatDate(sc.date, sc.ctx.locale, "weekday")} at ${formatTime(a.startsAt, timeZone)}` : undefined}>
        {a ? (
          <>
            <div className={s.facts}>
              <div><small>{t.appointments.drawer.when}</small><b>{formatTime(a.startsAt, timeZone)} – {formatTime(a.endsAt, timeZone)}</b></div>
              <div><small>{t.appointments.drawer.status}</small><b>{t.appointments.status[a.status]}</b></div>
              {a.staff ? <div><small>{t.appointments.drawer.with}</small><b>{a.staff.name}</b></div> : null}
              <div><small>{t.appointments.drawer.who}</small><b>{a.person.type === "lead" ? "Lead" : "Member"}</b></div>
            </div>
            <div className={s.sacts}>
              {actionsFor(a).map((x) => <Button key={x.action} variant={x.primary ? "primary" : "default"} busy={busy === x.action} busyLabel="Saving…" disabled={busy !== null} onClick={() => void go(x.action)}>{x.label}</Button>)}
            </div>
          </>
        ) : null}
      </BottomSheet>
    </>
  );
}

/* ------------------------------------------------------------------ tasks */

function MobileTasks({ sc, focusKey }: { sc: Scope; focusKey: string | null }) {
  const q = useTasks(sc);
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["tasks", ...sc.scopeKey, sc.date];
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [just, setJust] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (focusKey) input.current?.focus(); }, [focusKey]);
  const tasks = q.data ?? [];

  async function toggle(task: TaskItem, next: boolean) {
    qc.setQueryData<TaskItem[]>(key, (old) => old?.map((x) => (x.id === task.id ? { ...x, doneAt: next ? sc.nowMs : null } : x)));
    setJust(next ? task.id : null);
    try { await (next ? doneTask(client, task.id) : reopenTask(client, task.id)); } catch {
      qc.setQueryData<TaskItem[]>(key, (old) => old?.map((x) => (x.id === task.id ? { ...x, doneAt: task.doneAt } : x)));
      setJust(null);
      toast.show(t.tasks.failed);
    }
  }
  async function add(e: FormEvent) {
    e.preventDefault();
    const text = title.trim();
    if (!text || adding) return;
    setAdding(true);
    try {
      const res = await createTask(client, { title: text.slice(0, 140), dueDate: sc.date, branchId: sc.branchId ?? sc.scopeKey[1] ?? undefined });
      qc.setQueryData<TaskItem[]>(key, (old) => [res.task, ...(old ?? [])]);
      setTitle("");
    } catch { toast.show(t.tasks.addFailed); } finally { setAdding(false); }
  }

  return (
    <div className={s.grp}>
      <form className={s.qadd} onSubmit={add}>
        <input ref={input} value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} placeholder={t.tasks.addLabel} aria-label={t.tasks.addLabel} autoComplete="off" enterKeyHint="done" />
        <button type="submit" disabled={adding || !title.trim()}>{adding ? t.tasks.adding : t.tasks.add}</button>
      </form>
      {q.isPending ? <div aria-busy="true">{[0, 1, 2].map((i) => <div className={s.skRow} key={i}><Skeleton width={24} height={24} style={{ borderRadius: "50%" }} /><div><Skeleton width="70%" height={14} /></div></div>)}</div> : q.error ? (
        <FailureBody error={q.error} title={t.tasks.error} what="Tasks" onRetry={() => void q.refetch()} />
      ) : tasks.length === 0 ? <EmptyState compact icon="check" title={t.tasks.empty} description={t.tasks.emptyBody} /> : tasks.map((x) => {
        const done = x.doneAt !== null;
        const when = x.dueTime ? x.dueTime : x.dueDate === null || x.dueDate === sc.date ? null : formatDate(x.dueDate, sc.ctx.locale, "weekday");
        return (
          <div key={x.id} className={`${s.tk} ${done ? s.tkDone : ""}`} data-testid="task-row">
            <Tick checked={done} onChange={(next) => void toggle(x, next)} label={t.tasks.doneLabel(x.title)} justChecked={just === x.id} />
            <div className={s.tkBody}>
              <StrikeText done={done} animate={just === x.id}><span>{x.title}</span></StrikeText>
              <small>{x.assigneeStaffId ? <><StaffName id={x.assigneeStaffId} name={x.assigneeName} /> · </> : null}{done ? t.tasks.doneWord : when ? t.tasks.due(when) : t.tasks.dueToday}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}
